import Phaser from 'phaser';
import { t } from '../setup';
import { THEME } from '../config/theme';
import { isLevelCompleted } from '../utils/gameProgress';
import { createMenuButtonNav, ctaStyler, bindMenuKeys, type MenuButtonNav } from '../utils/menuButtonNav';
import { createGamepadMenuNav, type GamepadMenuNav } from '../utils/gamepadMenu';
import { resetGameScenes } from '../utils/sceneTransitions';
import { createMenuBackdrop, createMenuHeader, type MenuBackdrop } from '../systems/MenuTerrainRenderer';
import { playClick } from '../systems/UISounds';
import { generateValidDailyRunLevel, rankSeed, RANKS, type DailyRunRank } from '../systems/LevelGenerator';
import { seedToCode, codeToSeed, dailySeed, randomSeed } from '../utils/seededRNG';
import { DEPTHS } from '../config/gameConfig';
import { STORAGE_KEYS } from '../config/storageKeys';
import { startDailyRunSession } from '../systems/DailyRunSession';
import { getJSON } from '../utils/storage';
import { ResizeManager } from '../utils/resizeManager';
import { buildShareMessage, copyToClipboard } from '../utils/shareUrl';
import { showToast } from '../utils/toastNotification';
import { SCENE_KEYS } from '../config/sceneKeys';

const RANK_COLORS: Record<DailyRunRank, string> = {
  green: '#22c55e',
  blue: '#3b82f6',
  red: '#ef4444',
  black: '#1f2937',
};
const RANK_LABELS: Record<DailyRunRank, string> = {
  green: '●',
  blue: '■',
  red: '◆',
  black: '★',
};

export default class DailyRunsScene extends Phaser.Scene {
  private selectedRank: DailyRunRank = 'green';
  private greenIsPark = false;
  private rankButtons: Phaser.GameObjects.Text[] = [];
  private seedDisplay?: Phaser.GameObjects.Text;
  private pisteNameDisplay?: Phaser.GameObjects.Text;
  private briefingText?: Phaser.GameObjects.Text;
  private buttonNav?: MenuButtonNav;
  private gamepadNav?: GamepadMenuNav;
  private backdrop!: MenuBackdrop;
  private resizeManager!: ResizeManager;
  private sharedSeedNum: number | null = null;

  constructor() {
    super({ key: SCENE_KEYS.DAILY_RUNS });
  }

  create(data?: { seedCode?: string; rank?: DailyRunRank }): void {
    this.events.once('shutdown', this.shutdown, this);

    // Check for shared seed from URL params
    if (data?.seedCode) {
      this.sharedSeedNum = codeToSeed(data.seedCode);
      if (data.rank) this.selectedRank = data.rank;
    } else {
      this.sharedSeedNum = null;
    }

    const { width, height } = this.cameras.main;
    this.backdrop = createMenuBackdrop(this, { skipGroomer: true });
    const scaleFactor = this.backdrop.scaleFactor;

    const { backBtn } = createMenuHeader(this, 'dailyRuns', () => this.goBack(), scaleFactor);

    // Escape key always goes back (even when locked)
    this.input.keyboard?.on('keydown-ESC', () => this.goBack());

    // Check unlock: all 10 campaign levels must be completed
    const isUnlocked = this.isCampaignComplete();

    if (!isUnlocked) {
      this.showLockedMessage(width, height, scaleFactor);
      this.resizeManager = new ResizeManager(this);
      this.resizeManager.register();
      return;
    }

    this.buildDailyRunsUI(width, height, scaleFactor, backBtn);
  }

  private isCampaignComplete(): boolean {
    // Levels 1-10 (index 1-10) must all be completed
    for (let i = 1; i <= 10; i++) {
      if (!isLevelCompleted(i)) return false;
    }
    return true;
  }

  private showLockedMessage(width: number, height: number, scale: number): void {
    const dpr = window.devicePixelRatio || 1;
    const dprBoost = Math.sqrt(Math.min(dpr, 2));
    const textScale = Math.max(0.7, scale) * dprBoost;
    const fontSize = Math.round(16 * textScale);
    const smallFont = Math.round(13 * textScale);
    const tinyFont = Math.round(11 * textScale);
    const vGap = height < 450 ? 0.7 : 1.0;
    const wrapWidth = width * 0.85;

    // Analytical header bottom (same formula as buildDailyRunsUI)
    const titleTop = Math.round(height * 0.06);
    const titleFontSize = Math.max(16, Math.round(28 * scale * dprBoost));
    const headerBottom = titleTop + Math.round(titleFontSize * 1.2);

    if (this.sharedSeedNum !== null) {
      // Proportional layout: distribute preview + lock message in available space
      const available = Math.round(height * 0.85) - headerBottom;
      let yPos = headerBottom + Math.round(available * 0.08);

      const { level } = generateValidDailyRunLevel(rankSeed(this.sharedSeedNum, this.selectedRank), this.selectedRank);
      const pisteName = level.name || t(level.nameKey);
      const isPark = level.difficulty === 'park';
      const showPark = this.selectedRank === 'green' && isPark;
      const rankLabel = showPark ? '▲' : RANK_LABELS[this.selectedRank];
      const rankName = showPark ? t('rank_park') : t(`rank_${this.selectedRank}`);
      const rankColor = showPark ? '#f59e0b' : RANK_COLORS[this.selectedRank];

      // Rank badge
      this.add.text(width / 2, yPos, `${rankLabel} ${rankName}`, {
        fontFamily: THEME.fonts.family, fontSize: smallFont + 'px',
        color: THEME.colors.textPrimary, backgroundColor: rankColor,
        padding: { x: Math.round(12 * textScale), y: Math.round(6 * textScale) },
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
      yPos += Math.round(available * 0.12 * vGap);

      // Piste name
      this.add.text(width / 2, yPos, pisteName, {
        fontFamily: THEME.fonts.family, fontSize: fontSize + 'px',
        color: THEME.colors.textPrimary, fontStyle: 'bold',
        wordWrap: { width: wrapWidth },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
      yPos += Math.round(available * 0.10 * vGap);

      // Level characteristics
      const weatherKey = `dailyRuns_weather_${level.weather || 'clear'}`;
      const shiftKey = level.isNight ? 'dailyRuns_night' : 'dailyRuns_day';
      const parkStr = isPark ? ` | ▲ ${t('rank_park')}` : '';
      const winchStr = level.hasWinch ? ` | 🔗 ${t('winch')}` : '';
      this.add.text(width / 2, yPos, `${t(shiftKey)} | ${t(weatherKey)}${parkStr}${winchStr}`, {
        fontFamily: THEME.fonts.family, fontSize: tinyFont + 'px',
        color: THEME.colors.textMuted,
        wordWrap: { width: wrapWidth },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
      yPos += Math.round(available * 0.08 * vGap);

      // Briefing stats
      this.add.text(width / 2, yPos,
        `${t('dailyRuns_target')}: ${level.targetCoverage}% | ${t('dailyRuns_timeLimit')}: ${Math.floor(level.timeLimit / 60)}m${(level.timeLimit % 60) ? level.timeLimit % 60 + 's' : ''} | ${level.width}×${level.height}`,
        {
          fontFamily: THEME.fonts.family, fontSize: tinyFont + 'px',
          color: THEME.colors.textSecondary,
          wordWrap: { width: wrapWidth },
          align: 'center',
        }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
      yPos += Math.round(available * 0.07 * vGap);

      // Seed code reference
      const sharedCode = seedToCode(this.sharedSeedNum);
      this.add.text(width / 2, yPos, `[${sharedCode}]`, {
        fontFamily: THEME.fonts.family, fontSize: tinyFont + 'px',
        color: THEME.colors.textSecondary,
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
      yPos += Math.round(available * 0.15 * vGap);

      // Lock message
      this.add.text(width / 2, yPos, '🔒 ' + t('dailyRuns_lockedShared'), {
        fontFamily: THEME.fonts.family,
        fontSize: Math.round(14 * textScale) + 'px',
        color: THEME.colors.textSecondary,
        wordWrap: { width: wrapWidth },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
    } else {
      // Simple locked message centered in available space
      this.add.text(width / 2, Math.round(height * 0.4), '🔒 ' + t('dailyRuns_locked'), {
        fontFamily: THEME.fonts.family,
        fontSize: Math.round(14 * textScale) + 'px',
        color: THEME.colors.textSecondary,
        wordWrap: { width: wrapWidth },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);
    }
  }

  private buildDailyRunsUI(width: number, height: number, scale: number, backBtn: Phaser.GameObjects.Text): void {
    // Floor scale for text legibility; boost for high-DPR screens (same as MenuScene)
    const dpr = window.devicePixelRatio || 1;
    const dprBoost = Math.sqrt(Math.min(dpr, 2));
    const textScale = Math.max(0.7, scale) * dprBoost;
    const fontSize = Math.round(16 * textScale);
    const smallFont = Math.round(13 * textScale);
    const btnPadX = Math.round(20 * textScale);
    const btnPadY = Math.round(8 * textScale);

    // Get today's completed ranks
    const today = new Date().toISOString().slice(0, 10);
    const dailyData = getJSON<{ date: string; ranks: string[] }>(STORAGE_KEYS.DAILY_RUN_DATE, { date: '', ranks: [] });
    const completedRanks = dailyData.date === today ? dailyData.ranks : [];

    // --- Rank selector row ---
    const dailySeedNum = dailySeed();
    const { level: greenLevel } = generateValidDailyRunLevel(rankSeed(dailySeedNum, 'green'), 'green');
    this.greenIsPark = greenLevel.difficulty === 'park';

    // Proportional layout: anchor ranks below header, distribute remaining content with capped spread
    // Compute header bottom analytically (Phaser text dimensions aren't ready before first render)
    const titleTop = Math.round(height * 0.06);
    const titleFontSize = Math.max(16, Math.round(28 * scale * dprBoost));
    const titleBottom = titleTop + Math.round(titleFontSize * 1.2);
    const backBtnFontSize = Math.max(10, Math.round(13 * scale * dprBoost));
    const backBtnPadY = Math.round(6 * dprBoost);
    const backBtnBottom = titleTop + backBtnFontSize + 2 * backBtnPadY;
    const headerBottom = Math.max(titleBottom, backBtnBottom);
    // Rank buttons use origin(0.5) so they extend upward by half their height;
    // offset rankY so the button TOP sits 8px below the header
    const rankBtnHalfH = Math.round((smallFont + 2 * Math.round(6 * textScale)) / 2);
    const rankY = headerBottom + rankBtnHalfH + 8;
    const bottomBound = Math.round(height * 0.72);
    const available = Math.min(bottomBound - rankY, Math.round(height * 0.45));
    const briefingY = rankY + Math.round(available * 0.20);
    const dailyY = rankY + Math.round(available * 0.55);
    const randomY = rankY + Math.round(available * 0.75);

    const rankSpacing = Math.round(Math.min(100 * scale, (width - 40) / RANKS.length));
    const startX = width / 2 - (RANKS.length - 1) * rankSpacing / 2;

    this.rankButtons = RANKS.map((rank, i) => {
      const x = startX + i * rankSpacing;
      const done = completedRanks.includes(rank);
      const showPark = rank === 'green' && this.greenIsPark;
      const emoji = showPark ? '▲' : RANK_LABELS[rank];
      const name = showPark ? t('rank_park') : t(`rank_${rank}`);
      const label = done ? `${emoji} ${name} ✓` : `${emoji} ${name}`;
      const bgColor = showPark ? '#f59e0b' : RANK_COLORS[rank];
      const btn = this.add.text(x, rankY, label, {
        fontFamily: THEME.fonts.family,
        fontSize: smallFont + 'px',
        color: rank === this.selectedRank ? THEME.colors.textPrimary : THEME.colors.textMuted,
        backgroundColor: rank === this.selectedRank ? bgColor : THEME.colors.panelBgHex,
        padding: { x: Math.round(12 * textScale), y: Math.round(6 * textScale) },
        align: 'center',
      }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI).setInteractive({ useHandCursor: true });

      btn.on('pointerdown', () => {
        playClick();
        this.selectedRank = rank;
        this.updateRankSelection(completedRanks);
        this.updateBriefing();
      });
      return btn;
    });

    // Equalize rank button widths (measure with longest rank name + ✓)
    const longestRank = RANKS.reduce((a, b) => t(`rank_${a}`).length > t(`rank_${b}`).length ? a : b);
    const probeLabel = `${RANK_LABELS[longestRank]} ${t(`rank_${longestRank}`)} ✓`;
    const probe = this.add.text(0, 0, probeLabel, {
      fontFamily: THEME.fonts.family, fontSize: smallFont + 'px',
      padding: { x: Math.round(12 * textScale), y: Math.round(6 * textScale) },
    });
    // Cap button width so all 4 fit with gaps
    const maxAvailPerButton = Math.floor((width - 20) / RANKS.length) - 4;
    const maxW = Math.min(probe.width, maxAvailPerButton);
    probe.destroy();
    this.rankButtons.forEach(b => b.setFixedSize(maxW, 0));

    // Re-derive spacing from actual button width for centering
    const actualSpacing = Math.max(rankSpacing, maxW + 4);
    const actualStartX = width / 2 - (RANKS.length - 1) * actualSpacing / 2;
    this.rankButtons.forEach((b, i) => b.setX(actualStartX + i * actualSpacing));

    // --- Daily briefing (below rank row) ---
    // Evenly space the 3 briefing lines between briefingY and dailyY
    const briefingGap = Math.round((dailyY - briefingY) / 4);
    this.pisteNameDisplay = this.add.text(width / 2, briefingY, '', {
      fontFamily: THEME.fonts.family,
      fontSize: Math.round(13 * textScale) + 'px',
      color: THEME.colors.textPrimary,
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);

    this.seedDisplay = this.add.text(width / 2, briefingY + briefingGap, '', {
      fontFamily: THEME.fonts.family,
      fontSize: Math.round(11 * textScale) + 'px',
      color: THEME.colors.textMuted,
      align: 'center',
    }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);

    this.briefingText = this.add.text(width / 2, briefingY + briefingGap * 2, '', {
      fontFamily: THEME.fonts.family,
      fontSize: Math.round(11 * textScale) + 'px',
      color: THEME.colors.textSecondary,
      align: 'center',
    }).setOrigin(0.5).setDepth(DEPTHS.MENU_UI);

    this.updateBriefing();

    // --- Action buttons ---
    const allButtons: Phaser.GameObjects.Text[] = [];
    const allCallbacks: (() => void)[] = [];

    allButtons.push(backBtn);
    allCallbacks.push(() => this.goBack());

    const dailyCode = seedToCode(dailySeedNum);

    // Only show separate shared button when the seed differs from today's daily
    const isSharedDifferentFromDaily = this.sharedSeedNum !== null && this.sharedSeedNum !== dailySeedNum;

    // Shared seed button (when arriving via share URL with a non-daily seed)
    if (isSharedDifferentFromDaily) {
      const sharedCode = seedToCode(this.sharedSeedNum!);
      const sharedBtn = this.add.text(width / 2, dailyY,
        t('playSharedSeed') + '  [' + sharedCode + ']',
        {
          fontFamily: THEME.fonts.family,
          fontSize: fontSize + 'px',
          color: THEME.colors.textPrimary,
          backgroundColor: THEME.colors.buttonCTAHex,
          padding: { x: btnPadX, y: btnPadY },
        }
      ).setOrigin(0.5).setDepth(DEPTHS.MENU_UI).setInteractive({ useHandCursor: true });
      sharedBtn.setData('key', 'playSharedSeed');
      sharedBtn.on('pointerdown', () => { playClick(); this.startDailyRun(this.sharedSeedNum!); });
      allButtons.push(sharedBtn);
      allCallbacks.push(() => this.startDailyRun(this.sharedSeedNum!));
    }

    // Daily Shift button + share icon
    const dailyBtnY = isSharedDifferentFromDaily
      ? Math.round((dailyY + randomY) / 2)
      : dailyY;
    const isSecondaryCTA = isSharedDifferentFromDaily;

    const dailyBtn = this.add.text(width / 2, dailyBtnY,
      t('dailyRuns_dailyShift') + '  [' + dailyCode + ']',
      {
        fontFamily: THEME.fonts.family,
        fontSize: (isSecondaryCTA ? smallFont : fontSize) + 'px',
        color: isSecondaryCTA ? THEME.colors.textSecondary : THEME.colors.textPrimary,
        backgroundColor: isSecondaryCTA ? THEME.colors.textDark : THEME.colors.buttonCTAHex,
        padding: { x: isSecondaryCTA ? Math.round(14 * textScale) : btnPadX,
                   y: isSecondaryCTA ? Math.round(5 * textScale) : btnPadY },
      }
    ).setOrigin(0.5).setDepth(DEPTHS.MENU_UI).setInteractive({ useHandCursor: true });
    dailyBtn.setData('key', 'dailyShift');
    dailyBtn.on('pointerdown', () => { playClick(); this.startDailyRun(dailySeedNum, true); });
    allButtons.push(dailyBtn);
    allCallbacks.push(() => this.startDailyRun(dailySeedNum, true));

    // Share button (📋) — right of Daily Shift, min 44px touch target
    const sharePadX = Math.max(Math.round(10 * textScale), 14);
    const sharePadY = Math.max(isSecondaryCTA ? Math.round(5 * textScale) : btnPadY, 12);
    const shareBtn = this.add.text(0, dailyBtnY,
      '📋',
      {
        fontFamily: THEME.fonts.family,
        fontSize: (isSecondaryCTA ? smallFont : fontSize) + 'px',
        color: THEME.colors.textMuted,
        padding: { x: sharePadX, y: sharePadY },
      }
    ).setOrigin(0, 0.5).setDepth(DEPTHS.MENU_UI).setInteractive({ useHandCursor: true });
    shareBtn.setData('key', 'share');
    // Position right of daily button
    shareBtn.setX(Math.round(dailyBtn.x + dailyBtn.width / 2 + 6 * textScale));
    shareBtn.on('pointerdown', () => {
      playClick();
      const pisteName = this.pisteNameDisplay?.text || '';
      const msg = buildShareMessage(dailyCode, this.selectedRank, pisteName);
      copyToClipboard(msg).then(ok => { if (ok) showToast(this, t('copied')); });
    });

    // --- Separator + Random Run (hide when shared seed takes the daily spot) ---
    if (!isSharedDifferentFromDaily) {
      const sepY = Math.round((dailyY + randomY) / 2);
      const lineW = Math.round(width * 0.25);
      const sepGfx = this.add.graphics().setDepth(DEPTHS.MENU_UI);
      sepGfx.fillStyle(0x666666, 0.4);
      sepGfx.fillRect(width / 2 - lineW / 2, sepY, lineW, 1);
    }

    const randomBtn = this.add.text(width / 2, randomY,
      t('dailyRuns_randomRun'),
      {
        fontFamily: THEME.fonts.family,
        fontSize: smallFont + 'px',
        color: THEME.colors.textSecondary,
        backgroundColor: THEME.colors.textDark,
        padding: { x: Math.round(14 * textScale), y: Math.round(5 * textScale) },
      }
    ).setOrigin(0.5).setDepth(DEPTHS.MENU_UI).setInteractive({ useHandCursor: true });
    randomBtn.on('pointerdown', () => {
      playClick();
      this.startDailyRun(randomSeed());
    });
    allButtons.push(randomBtn);
    allCallbacks.push(() => {
      this.startDailyRun(randomSeed());
    });

    // Keyboard/gamepad navigation: up/down for action buttons, left/right for rank
    // isCTA: back=false, [shared=true if present], daily=CTA when no shared, random=false
    const isCTA = isSharedDifferentFromDaily
      ? [false, true, false, false]  // back, shared(CTA), daily, random
      : [false, true, false];        // back, daily(CTA), random
    this.buttonNav = createMenuButtonNav(allButtons, allCallbacks, ctaStyler(isCTA));
    this.buttonNav.select(1); // Start on first action button (shared or daily)

    // Left/right arrows cycle rank, standard keys for button nav
    this.input.keyboard?.on('keydown-LEFT', () => this.cycleRank(-1));
    this.input.keyboard?.on('keydown-RIGHT', () => this.cycleRank(1));
    bindMenuKeys(this, this.buttonNav);

    // Pointer hover selects buttons in nav
    allButtons.forEach((btn, i) => {
      btn.on('pointerover', () => this.buttonNav!.select(i));
    });

    this.gamepadNav = createGamepadMenuNav(this, 'vertical', {
      onNavigate: (dir) => this.buttonNav!.navigate(dir),
      onConfirm: () => this.buttonNav!.activate(),
      onBack: () => this.goBack(),
    });

    this.resizeManager = new ResizeManager(this);
    this.resizeManager.register();
  }

  private cycleRank(dir: number): void {
    const idx = RANKS.indexOf(this.selectedRank);
    const next = (idx + dir + RANKS.length) % RANKS.length;
    this.selectedRank = RANKS[next];
    playClick();
    this.updateRankSelection();
    this.updateBriefing();
  }

  private updateRankSelection(completedRanks?: string[]): void {
    this.rankButtons.forEach((btn, i) => {
      const rank = RANKS[i];
      const selected = rank === this.selectedRank;
      const done = completedRanks ? completedRanks.includes(rank) : btn.text.includes('✓');
      // Show park-themed label when green rank has a park level
      const showPark = rank === 'green' && this.greenIsPark;
      const name = showPark ? t('rank_park') : t(`rank_${rank}`);
      const emoji = showPark ? '▲' : RANK_LABELS[rank];
      const label = done ? `${emoji} ${name} ✓` : `${emoji} ${name}`;
      const bgColor = showPark ? '#f59e0b' : RANK_COLORS[rank];
      btn.setText(label);
      btn.setColor(selected ? THEME.colors.textPrimary : THEME.colors.textMuted);
      btn.setBackgroundColor(selected ? bgColor : THEME.colors.panelBgHex);
    });
  }

  private updateBriefing(): void {
    const baseSeed = this.sharedSeedNum ?? dailySeed();
    const { level } = generateValidDailyRunLevel(rankSeed(baseSeed, this.selectedRank), this.selectedRank);
    const isPark = level.difficulty === 'park';

    if (this.pisteNameDisplay) {
      this.pisteNameDisplay.setText(level.name || t(level.nameKey));
    }

    const weatherKey = `dailyRuns_weather_${level.weather || 'clear'}`;
    const shiftKey = level.isNight ? 'dailyRuns_night' : 'dailyRuns_day';
    const parkStr = isPark ? ` | ▲ ${t('rank_park')}` : '';
    const winchStr = level.hasWinch ? ` | 🔗 ${t('winch')}` : '';

    if (this.seedDisplay) {
      this.seedDisplay.setText(
        `${t(shiftKey)} | ${t(weatherKey)}${parkStr}${winchStr}`
      );
    }
    if (this.briefingText) {
      this.briefingText.setText(
        `${t('dailyRuns_target')}: ${level.targetCoverage}% | ${t('dailyRuns_timeLimit')}: ${Math.floor(level.timeLimit / 60)}m${(level.timeLimit % 60) ? level.timeLimit % 60 + 's' : ''} | ${level.width}×${level.height}`
      );
    }
    // Update green button to show park theme when the daily level is a park
    this.updateRankSelection();
  }

  private startDailyRun(seed: number, isDaily = false): void {
    const { level, usedSeed } = generateValidDailyRunLevel(rankSeed(seed, this.selectedRank), this.selectedRank);
    const code = seedToCode(usedSeed);

    startDailyRunSession({
      level,
      seedCode: code,
      baseSeedCode: seedToCode(seed),
      rank: this.selectedRank,
      isDaily,
    });

    resetGameScenes(this.game, SCENE_KEYS.GAME, { level: level.id });
  }

  private goBack(): void {
    resetGameScenes(this.game, SCENE_KEYS.MENU);
  }

  update(time: number, delta: number): void {
    this.gamepadNav?.update(delta);
    this.backdrop.wildlife?.update(time, delta);
  }

  private shutdown(): void {
    this.resizeManager?.destroy();
    this.buttonNav = undefined;
    this.gamepadNav = undefined;
    this.backdrop.wildlife?.destroy();
    this.input.keyboard?.removeAllListeners();
  }
}
